/**
 * ModoForjaOverlay - Full screen immersive focus experience
 * Premium design with click-outside-to-minimize
 */

import React, { useEffect } from 'react';
import { Minimize2, Volume2, VolumeX, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useModoForja } from '@/contexts/ModoForjaContext';
import ModoForjaTimer from './ModoForjaTimer';
import ModoForjaRadio from './ModoForjaRadio';
import ModoForjaSettings from './ModoForjaSettings';

export default function ModoForjaOverlay() {
  const {
    isOpen,
    minimizeModoForja,
    endModoForja,
    needsEndConfirmation,
    confirmEnd,
    cancelEnd,
    soundEnabled,
    toggleSound,
    isRunning,
    sessionType,
  } = useModoForja();

  // Handle Escape key - now minimizes instead of closing
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        minimizeModoForja();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, minimizeModoForja]);

  // Prevent scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);


  if (!isOpen) return null;

  return (
    <>
      {/* Full screen overlay - clicking backdrop minimizes */}
      <div
        className={cn(
          "fixed inset-0 z-[100] flex items-center justify-center",
          "bg-black/95 backdrop-blur-2xl",
          "animate-in fade-in duration-300"
        )}
        onClick={minimizeModoForja}
      >
        {/* Top left - Sound toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => { e.stopPropagation(); toggleSound(); }}
          className="absolute top-4 left-4 w-9 h-9 rounded-full text-muted-foreground/60 hover:text-foreground hover:bg-white/5"
        >
          {soundEnabled ? (
            <Volume2 className="w-4 h-4" />
          ) : (
            <VolumeX className="w-4 h-4" />
          )}
        </Button>

        {/* Top right - Minimize and End buttons */}
        <div className="absolute top-4 right-4 flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); minimizeModoForja(); }}
            className="w-9 h-9 rounded-full text-muted-foreground/60 hover:text-foreground hover:bg-white/5"
            title="Minimizar"
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); endModoForja(); }}
            className="w-9 h-9 rounded-full text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10"
            title="Encerrar Sessão"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Main container - premium design with glow */}
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "relative w-full max-w-sm mx-4 p-5 sm:p-6",
            "bg-gradient-to-b from-card/98 to-card/95",
            "rounded-[1.75rem] border border-white/[0.08]",
            "shadow-2xl",
            "animate-in zoom-in-95 duration-300",
            // Dynamic glow based on session type
            sessionType === 'focus' 
              ? "shadow-primary/20" 
              : "shadow-success/20"
          )}
          style={{
            boxShadow: sessionType === 'focus'
              ? '0 0 60px -10px hsl(var(--primary) / 0.25), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              : '0 0 60px -10px hsl(var(--success) / 0.25), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Subtle inner glow */}
          <div 
            className={cn(
              "absolute inset-0 rounded-[1.75rem] opacity-50 pointer-events-none",
              sessionType === 'focus'
                ? "bg-gradient-to-b from-primary/5 to-transparent"
                : "bg-gradient-to-b from-success/5 to-transparent"
            )}
          />

          {/* Header - minimal and elegant */}
          <div className="relative text-center mb-4">
            <h2 className="font-display text-lg font-semibold text-foreground/90 tracking-wide">
              Modo <span className={cn(
                "bg-clip-text text-transparent bg-gradient-to-r",
                sessionType === 'focus' 
                  ? "from-primary to-primary/70"
                  : "from-success to-success/70"
              )}>Forja</span>
            </h2>
          </div>

          {/* Timer */}
          <div className="relative">
            <ModoForjaTimer />
          </div>

          {/* Radio Player */}
          <div className="relative mt-6">
            <ModoForjaRadio />
          </div>

          {/* Settings */}
          <div className="relative mt-3">
            <ModoForjaSettings />
          </div>
        </div>
      </div>

      {/* End confirmation dialog */}
      <AlertDialog open={needsEndConfirmation} onOpenChange={cancelEnd}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar o Modo Forja?</AlertDialogTitle>
            <AlertDialogDescription>
              Seu ciclo atual será finalizado e o tempo não será contabilizado como sessão completa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelEnd}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmEnd}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Encerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
